/**
 * Deep research report job status (queued / running / completed / failed) shown inline
 * under assistant chat bubbles. Used by ChatAssistant, NeoAssistant (ticket + shared),
 * AI Thread Observer, and the browser extension so behavior stays consistent.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import researchReportJobsApi, { type ResearchReportJob } from "../../apis/researchReportJobsApi";
import type { ReportAttachmentTokenPayload } from "../utils/reportAttachmentTokens";

/** Matches Cortex research queued messages; same pattern everywhere. */
const RESEARCH_JOB_ID_REGEX = /research report job\s+(\d+)/i;

export function extractResearchJobId(rawContent: unknown): number | null {
  const content = String(rawContent || "");
  const match = content.match(RESEARCH_JOB_ID_REGEX);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function buildReportHref(jobId: number, reportBaseUrl?: string | null): string {
  const path = `/report/${encodeURIComponent(String(jobId))}`;
  const base = String(reportBaseUrl || "").trim().replace(/\/$/, "");
  if (!base) return path;
  try {
    return new URL(path, base.endsWith("/") ? base : `${base}/`).href;
  } catch {
    return `${base}${path}`;
  }
}

export type ResearchReportJobStatusInlineProps = {
  jobId: number;
  aiThreadId: string | number | null | undefined;
  /** When false, do not poll or render. */
  pollEnabled?: boolean;
  /** Absolute web app origin for “Open report” in extension / embedded contexts (e.g. VITE_REACT_APP_DOMAIN). */
  reportBaseUrl?: string | null;
  /** Adds a completed report to the active chat composer; omit on surfaces without a composer. */
  onAttachReport?: (report: ReportAttachmentTokenPayload) => void;
};

export const ResearchReportJobStatusInline: React.FC<ResearchReportJobStatusInlineProps> = ({
  jobId,
  aiThreadId,
  pollEnabled = true,
  reportBaseUrl = null,
  onAttachReport,
}) => {
  const [job, setJob] = useState<ResearchReportJob | null>(null);

  const threadNum = Number(aiThreadId);
  const threadOk = Number.isFinite(threadNum) && threadNum > 0;

  const loadStatuses = useCallback(async () => {
    if (!pollEnabled || !threadOk) return;
    try {
      const response = await researchReportJobsApi.listJobs({
        job_id: String(jobId),
        ai_thread_id: String(threadNum),
        limit: 1,
        offset: 0,
      });
      const row = Array.isArray(response.jobs) ? response.jobs[0] || null : null;
      if (row && Number(row.ai_thread_id) === threadNum) {
        setJob(row);
      } else {
        setJob(null);
      }
    } catch {
      // Keep last known job on transient errors.
    }
  }, [pollEnabled, threadOk, jobId, threadNum]);

  useEffect(() => {
    if (!pollEnabled || !threadOk) {
      setJob(null);
      return;
    }
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const run = async () => {
      if (cancelled) return;
      await loadStatuses();
    };
    run();
    intervalId = setInterval(run, 10000);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [loadStatuses, pollEnabled, threadOk]);

  if (!pollEnabled || !threadOk) {
    return null;
  }

  if (!job) {
    return (
      <Typography sx={{ mt: 0.75, fontSize: 12, color: "#666F8D" }}>
        Job status: checking...
      </Typography>
    );
  }

  if (Number(job.ai_thread_id) !== threadNum) {
    return null;
  }

  if (job.status === "running") {
    const progress = Math.max(0, Math.min(100, Number(job.progress_percent || 0)));
    return (
      <Box sx={{ mt: 1, maxWidth: 320 }}>
        <Typography sx={{ fontSize: 12, color: "#666F8D", mb: 0.5 }}>
          Job status: running ({Math.round(progress)}%)
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 10,
            borderRadius: 999,
            backgroundColor: "#E5E7EB",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#1976d2",
            },
          }}
        />
      </Box>
    );
  }

  if (job.status === "failed") {
    const reason =
      String(job.client_error_message || "").trim() || "The report failed. Please retry.";
    return (
      <Box
        sx={{
          mt: 0.9,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          maxWidth: 560,
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#666F8D" }}>Job status:</Typography>
        <Chip size="small" label={job.status} color="error" variant="outlined" />
        <Typography
          component="span"
          sx={{ fontSize: 12, color: "error.main", lineHeight: 1.35 }}
          title={reason}
        >
          {reason}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 0.9, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: 12, color: "#666F8D" }}>Job status:</Typography>
      <Chip
        size="small"
        label={job.status}
        color={
          job.status === "completed"
            ? "success"
            : job.status === "queued"
            ? "default"
            : "warning"
        }
        variant="outlined"
      />
      {job.status === "completed" ? (
        <>
          {onAttachReport ? (
            <Button
              type="button"
              size="small"
              variant="outlined"
              startIcon={<AttachFileIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                const title =
                  String(job.report_title || job.request_text || `Report ${job.job_id}`).trim() ||
                  `Report ${job.job_id}`;
                onAttachReport({
                  jobId: job.job_id,
                  title,
                  completedAt: job.completed_at || null,
                  customerId: job.customer_id ?? null,
                });
              }}
              sx={{
                fontSize: 11,
                fontFamily: "var(--theo-font-sans)",
                fontWeight: 600,
                textTransform: "none",
                color: "#374151",
                borderColor: "#D1D5DB",
                borderRadius: 999,
                px: 1.2,
                py: 0.15,
                minHeight: 24,
                lineHeight: 1.1,
                "&:hover": {
                  borderColor: "#9CA3AF",
                  color: "#111827",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              Attach report
            </Button>
          ) : null}
          <Button
            component="a"
            href={buildReportHref(job.job_id, reportBaseUrl)}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontSize: 11,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 600,
              textTransform: "none",
              color: "#1D4ED8",
              borderColor: "#93C5FD",
              borderRadius: 999,
              px: 1.2,
              py: 0.15,
              minHeight: 24,
              lineHeight: 1.1,
              "&:hover": {
                borderColor: "#2563EB",
                color: "#1E40AF",
                backgroundColor: "#EFF6FF",
              },
            }}
          >
            Open report
          </Button>
        </>
      ) : null}
    </Box>
  );
};
